/**
 * Точки прибытия: куда именно ведём линию у цели — нарисованная дверь, ближайшая
 * точка на контуре или «вход не нарисован» (честный фолбэк), плюс узлы-цели для
 * графа. Вынесено из route-access.ts: там выбор ПРОФИЛЯ доступа, здесь — точки.
 */
import type { LonLat } from '@app/geo'
import type { RouteProfile } from './graph'
import { entranceDoorIds, unitTargetNodeIds } from './route-access'
import { nearestAccessNodeId, nearestPointOnRing, unitCentroid, unitContaining } from './route-geom'
import type { Venue } from './types'

/** Точка первой двери-входа юнита (для «дожима» пунктиром к входу). */
function firstDoorPoint(
  venue: Venue,
  unit: Venue['units'][number],
  profile: RouteProfile,
): LonLat | null {
  for (const id of entranceDoorIds(venue, unit, profile)) {
    const node = venue.network.nodes.find((n) => n.id === id)
    if (node) return node.point
  }
  return unitCentroid(unit)
}

/**
 * Геометрическая точка прибытия — куда тянется финальный пунктир egress:
 * POI — своя точка (внутрь помещения); здание — точка ближайшей ДВЕРИ.
 */
export function arrivalPoint(
  venue: Venue,
  targetId: string,
  profile: RouteProfile = 'foot',
): LonLat | null {
  const poi = venue.pois.find((p) => p.id === targetId)
  if (poi) return poi.point
  const unit = venue.units.find((u) => u.id === targetId)
  if (unit) return firstDoorPoint(venue, unit, profile)
  const node = venue.network.nodes.find((n) => n.id === targetId)
  return node ? node.point : null
}

/**
 * Здание-цель БЕЗ нарисованной двери (запрос владельца 27.07): рисовать линию
 * СКВОЗЬ здание к точке внутри — обман, «прохода через здание нет». Пока вход не
 * отмечен, доводим маршрут до КРАЯ контура — ближайшей к `from` (концу сети)
 * точки стены, а не до точки внутри. Возвращает эту точку-«у стены» или null,
 * если у здания есть дверь / цель не в здании (тогда — обычная логика).
 */
export function undrawnEntrancePoint(venue: Venue, targetId: string, from: LonLat): LonLat | null {
  let host = venue.units.find((u) => u.id === targetId) ?? null
  if (!host) {
    const poi = venue.pois.find((p) => p.id === targetId)
    if (poi) host = unitContaining(venue, poi)
  }
  // «Есть вход» = есть пешеходная дверь (пешеход всегда заходит через неё).
  if (!host || entranceDoorIds(venue, host, 'foot').length > 0) return null
  const ring = host.geometry.coordinates[0]
  return ring && ring.length >= 2 ? nearestPointOnRing(from, ring) : null
}

/**
 * Точка, к которой ЦЕЛИТСЯ сеть (фолбэк «частично пешком»): всегда ДВЕРЬ-вход —
 * даже для арендатора внутри здания (внутрь только через вход). Так авто/фура,
 * не доехав до парковки, выходят с дороги у ВХОДА, а не срезают к точке цели.
 */
export function entranceAimPoint(
  venue: Venue,
  targetId: string,
  profile: RouteProfile = 'foot',
): LonLat | null {
  const poi = venue.pois.find((p) => p.id === targetId)
  if (poi) {
    const host = unitContaining(venue, poi)
    if (host) return firstDoorPoint(venue, host, profile)
    return poi.point
  }
  return arrivalPoint(venue, targetId, profile)
}

export function targetNodeIds(
  venue: Venue,
  targetId: string,
  profile: RouteProfile = 'foot',
): string[] {
  const unit = venue.units.find((u) => u.id === targetId)
  if (unit) return unitTargetNodeIds(venue, unit, profile)
  const poi = venue.pois.find((p) => p.id === targetId)
  if (poi) {
    const linked = venue.network.nodes.find((n) => n.poi === targetId)
    if (linked) return [linked.id]
    // Арендатор ВНУТРИ здания: маршрут к двери здания (по профилю), финальное
    // плечо до точки дорисует planRoute — а не к случайному ближайшему узлу
    // (маршрут «не так» — фидбек владельца 26.07).
    const host = unitContaining(venue, poi)
    if (host) {
      const doors = unitTargetNodeIds(venue, host, profile)
      if (doors.length > 0) return doors
    }
    const nearest = nearestAccessNodeId(venue, poi.point, poi.level)
    return nearest ? [nearest] : []
  }
  // Узел графа как цель (клик по двери/рампе во вьюере) — прямо к нему.
  if (venue.network.nodes.some((n) => n.id === targetId)) return [targetId]
  return []
}
